import { Button } from "@/components/ui/button";
import { ArrowUpRight, Sparkles, TrendingUp } from "lucide-react";

export type DossierItem = {
  num: string;
  title: string;
  text: string;
};

type EditorialPageHeroProps = {
  kicker: string;
  eyebrow?: string;
  title: React.ReactNode;
  highlight?: string;
  lead?: string;
  description?: string;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  note?: string;
  dossierTitle?: string;
  dossier?: DossierItem[];
  video?: boolean;
};

/**
 * Portada editorial reutilizable: misma retícula, tipografía y jerarquía
 * dorada que el hero de la home. Se usa como cabecera de todas las páginas.
 */
export function EditorialPageHero({
  kicker,
  eyebrow,
  title,
  highlight,
  lead,
  description,
  primaryCta,
  secondaryCta,
  note,
  dossierTitle = "Cómo trabajamos",
  dossier,
  video = true,
}: EditorialPageHeroProps) {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-32 pb-14 md:pb-20">
      {video && (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          style={{ willChange: "transform" }}
          src="/hero-bg.mp4"
          autoPlay
          loop
          muted
          playsInline
          onError={(e) => {
            (e.target as HTMLVideoElement).style.display = "none";
          }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0912]/92 via-[#171326]/88 to-[#0B0912]/94" />
      <div className="absolute top-0 left-1/3 w-[520px] h-[380px] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[280px] h-[280px] bg-premium-gold/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-premium-gold/25 pb-4">
          <span className="text-[11px] uppercase tracking-[0.32em] text-premium-gold font-semibold">{kicker}</span>
          {eyebrow && (
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-premium-gold" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-white/70">{eyebrow}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 pt-10 md:pt-14">
          <div className={dossier?.length ? "lg:col-span-7" : "lg:col-span-9"}>
            <h1
              className="font-black leading-[0.97] tracking-tight text-white text-[2.3rem] sm:text-5xl xl:text-6xl animate-slide-in-up"
              style={{ animationDelay: "0.05s" }}
            >
              {title}
              {highlight && <span className="block text-premium-gold">{highlight}</span>}
            </h1>

            {lead && (
              <div className="mt-8 border-l-2 border-primary/70 pl-5 animate-slide-in-up" style={{ animationDelay: "0.15s" }}>
                <p className="text-lg md:text-xl font-semibold text-primary leading-snug">{lead}</p>
              </div>
            )}

            {description && (
              <p
                className="mt-6 text-muted-foreground text-base leading-relaxed max-w-xl animate-slide-in-up"
                style={{ animationDelay: "0.25s" }}
              >
                {description}
              </p>
            )}

            {(primaryCta || secondaryCta) && (
              <div
                className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 animate-slide-in-up"
                style={{ animationDelay: "0.35s" }}
              >
                {primaryCta && (
                  <Button
                    asChild
                    size="lg"
                    className="group bg-gradient-to-r from-primary to-accent px-8 py-6 text-base transition-all duration-300 hover:shadow-2xl hover:shadow-primary/40"
                  >
                    <a href={primaryCta.href} className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                      {primaryCta.label}
                    </a>
                  </Button>
                )}
                {secondaryCta && (
                  <a
                    href={secondaryCta.href}
                    className="group inline-flex items-center gap-2 text-base font-semibold text-white border-b border-premium-gold/40 pb-1 hover:border-premium-gold transition-colors"
                  >
                    {secondaryCta.label}
                    <ArrowUpRight className="w-4 h-4 text-premium-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                )}
              </div>
            )}

            {note && <p className="mt-6 text-sm text-white/55">{note}</p>}
          </div>

          {dossier?.length ? (
            <aside className="lg:col-span-5 xl:col-span-4 xl:col-start-9 animate-slide-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="border-t border-premium-gold/30 pt-5">
                <p className="text-[11px] uppercase tracking-[0.28em] text-premium-gold font-semibold mb-2">
                  {dossierTitle}
                </p>
                <ul className="divide-y divide-white/10">
                  {dossier.map((item) => (
                    <li key={item.num} className="group py-5 flex gap-5">
                      <span className="font-black text-2xl leading-none text-white/15 group-hover:text-premium-gold/70 transition-colors">
                        {item.num}
                      </span>
                      <div>
                        <p className="text-base font-bold text-white">{item.title}</p>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.text}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </section>
  );
}
